/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

export type DecodedToken = { login_hint: string, tenant_id: string, family_name: string, given_name: string, scope: string, act?: { tenant_id?: string }, idp?: string };

export const iam = {
  // these need to be loaded prior to this const being instantiated, except in the case of unit tests which will manually set them
  // NOTE: these aren't intended to be all-inclusive. if one is missing, it just means it hasn't been referenced in typescript files yet. add properties when needed.
  settings: (window as any).iam?.settings as {
    signInPage: string,
    authServer: string,
    domain: string,
    clientID: string,
    pkceClient: string,
    usePKCE: boolean,
    mcafeeIdp: string,
    scopeJson: string,
    appURL: string,
    customPostLoginRedirect: string,
    consoleURL: string,
    usersURL: string,
    uamBackendURL: string,
    idpUrl: string,
    notificationJson: string | null,
    sharedCookieDomain: string,
    govCloud: boolean,
    govCloudCA: boolean,
    signUpURL: string,
    useTPS?: boolean, // optional: unset is treated as enabled (see `=== false` gate), so configs need not define it
    tpsURL?: string, // optional: only required/read when useTPS !== false
    tpsResolverURL?: string, // optional: only required/read when useTPS !== false
    epoPartitionPath: string,
    blockLogin: boolean,
    siteMigrationRedirect: string | null,
    branding: 'trellix' | 'skyhigh' | 'both',
    appendTokenUrls: string[],
    fallbackWhitelistURL: { domain: string, subdomains?: boolean, protocolRE?: string, portRE?: string }[],
    i18n: { [lang: string]: { [key: string]: string } },
    languageOptions: { countryLang: string, langCode: string }[]
    debug: boolean,
    redirectUrl: string,
    pkceAuthURL: string,
    discovery: string,
    signInHelp: string,
    alreadySignedIn: string,
    localization: string,
    tokenHandler: string,
    uamProfile: string,
    uamManagement: string,
    subscription: string,
    idpConfig: string,
    uamRegistration: string,
    uamClientCreds: string,
    uamOrgAccount: string,
    uamAuditLog: string,
    displaySignup: boolean,
    testNewStrings: boolean,
    deprecatedFactors: string[] | null
  },
  Token: (window as any).iam?.Token as {
    getCookie: (cname: string) => string,
    setCookie: (cname: string, cvalue: string) => void,
    getSessionCookie: () => Promise<{ login_hint: string, tenant_id: string } | undefined>,
    setSessionCookie: (email: string, tenantId: string) => Promise<void>,
    removeSessionCookie: () => Promise<void>,
    getItem: (name: string) => string,
    setItem: (name: string, value: string | Date) => void,
    removeItem: (name: string) => void,
    getWhitelistURL: () => { origin: RegExp, allow: ('get' | 'set' | 'del' | 'clear')[] }[],
    getLocation: () => { origin: string, pathname: string, href: string, search: string, hash: string },
    setLocation: (location: string) => void,
    parseToken: (token: string) => DecodedToken,
    getScopes: () => Promise<string>,
    _loadScopes: (url: string, callback: (err: Error | null, data?: string) => void) => void,
    scheduleRenewal: () => void,
    tokenRenewalTimeoutAuth: null | number,
    permittedURL: (origin: string) => boolean,
    getHashParams: (fragment: string) => { [key: string]: string },
    getTokenPromise: (options?: { init?: boolean, redirectUrl?: string, pkceRedirectUrl?: string, returnEmptyToken?: boolean, tokenChangeHandler?: (object: { currentToken: string, previousToken: string, currentDecodedToken: string, previousDecodedToken: string, reason: string }) => void, additionalReloadReferrers?: [] }) => Promise<{ accessToken: string, accessTokenExpires: string } | undefined>,
    getTenantDetails: (emailId: string) => Promise<{ tenantId: string, companyName: string, type: string }[]>,
    logout: (postTokenCleanFunction?: (expired: boolean) => void) => void,
    tokenExchange: (forceRefresh: boolean, tenantId: string) => Promise<void>,
    listenForTokenChanges: (cb: (object: { currentToken: string, previousToken: string, currentDecodedToken: string, previousDecodedToken: string, reason: string }) => void) => void,
    tokenExpired: (accessTokenExpired?: string) => boolean,
    setupNavigatorLock: (func: () => void) => Promise<any>,
    generateRandomString: () => string,
    pkceChallengeFromVerifier: (v: string) => Promise<string>,
    pkceRedirectionInProgress: boolean,
    getDecodedToken: (token?: string) => DecodedToken | undefined
  },
  consts: (window as any).iam?.consts as {
    hashKeys: {
      usePKCE: string,
      tId: string
    },
    storageKeys: {
      usePKCE: string,
      tId: string
    }
  } | undefined
};
